import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import './CommunityManagement.css'; // Changed from AdminPanel.css
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faDownload, faEye, faUsers } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet';

function CommunityManagement() {
    const [communityCategories, setCommunityCategories] = useState([]);
    const [categoryName, setCategoryName] = useState("");
    const [categoryAppid, setCategoryAppid] = useState("");
    const [categoryIsNSFW, setCategoryIsNSFW] = useState(false);
    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [communityError, setCommunityError] = useState("");
    const [selectedTableDetails, setSelectedTableDetails] = useState(null);
    const [message, setMessage] = useState("");
    const [actionLoading, setActionLoading] = useState({});
    const navigate = useNavigate();
    // Reports state
    const [reports, setReports] = useState([]);
    const [reportsLoading, setReportsLoading] = useState(false);
    const [reportsError, setReportsError] = useState("");

    // Reason label map
    const reasonLabel = {
    not_working: "Table not working",
    breaks_game: "Table breaks/bugs game",
    malicious: "Table has malicious functions",
    stolen: "This table is stolen",
    other: "Other",
    };

    // Check admin status on mount
    const checkAdminStatus = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            navigate("/login");
            return;
        }

        try {
            const res = await axios.get("/api/auth/check-admin", {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            if (!res.data.isAdmin) {
                navigate("/login");
            }
        } catch (error) {
            console.error("Error verifying admin status:", error);
            navigate("/login");
        }
    };

    const fetchReports = async () => {
    setReportsLoading(true);
    setReportsError("");
    try {
        const res = await axios.get("/api/community/reports", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setReports(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
        setReportsError(err?.response?.data?.error || "Failed to fetch reports.");
    } finally {
        setReportsLoading(false);
    }
    };

    const fetchCommunityCategories = async () => {
        try {
            const response = await axios.get("/api/community/categories", {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                params: { status: 'all' }
            });
            const categories = response.data.filter(cat => cat.tables && cat.tables.length > 0);
            console.log('Fetched community categories:', categories.map(cat => ({
                name: cat.name,
                tableCount: cat.tables.length,
                tableStatuses: cat.tables.map(t => t.status)
            })));
            setCommunityCategories(categories);
        } catch (err) {
            setCommunityError("Failed to fetch categories.");
            console.error('Error fetching categories:', err.response?.data || err.message);
        }
    };

    const handleCommunityCategorySubmit = async (e) => {
        e.preventDefault();
        setCommunityError("");
        try {
            const payload = { name: categoryName, appid: categoryAppid, isNSFW: categoryIsNSFW };
            const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
            let response;
            if (editingCategoryId) {
                response = await axios.put(`/api/community/categories/${editingCategoryId}`, payload, { headers });
                setCommunityCategories(communityCategories.map((cat) => (cat._id === editingCategoryId ? response.data : cat)));
                setEditingCategoryId(null);
            } else {
                response = await axios.post("/api/community/categories", payload, { headers });
                setCommunityCategories([...communityCategories, response.data]);
            }
            setCategoryName("");
            setCategoryAppid("");
            setCategoryIsNSFW(false);
            setMessage({ text: editingCategoryId ? "Category updated successfully." : "Category added successfully.", type: "success" });
        } catch (err) {
            setCommunityError(err.response?.data?.error || "Failed to save category.");
        }
    };

    const handleEditCommunityCategory = (category) => {
        setEditingCategoryId(category._id);
        setCategoryName(category.name);
        setCategoryAppid(category.appid || "");
        setCategoryIsNSFW(category.isNSFW);
        setCommunityError("");
    };

    const handleDeleteCommunityCategory = async (id) => {
        if (!window.confirm("Are you sure you want to delete this category?")) return;
        setCommunityError("");
        try {
            await axios.delete(`/api/community/categories/${id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            setCommunityCategories(communityCategories.filter((cat) => cat._id !== id));
            setMessage({ text: "Category deleted successfully.", type: "success" });
        } catch (err) {
            setCommunityError(err.response?.data?.error || "Failed to delete category.");
        }
    };

    const handleViewTableDetails = (table) => {
        setSelectedTableDetails({
            ...table,
            originalTableId: table.originalTableId || null
        });
    };

    const closeTableDetailsModal = () => {
        setSelectedTableDetails(null);
    };

    const handleVerifyCommunityTable = async (id, status) => {
        setCommunityError("");
        try {
            console.log(`Attempting to verify table ID: ${id} with status: ${status}`);
            const response = await axios.put(
                `/api/community/${id}/verify`,
                { status },
                { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
            );
            await fetchCommunityCategories();
            setMessage({ text: response.data.message, type: "success" });
            setSelectedTableDetails(null);
        } catch (err) {
            console.error(`Error verifying table ID: ${id}`, err.response?.data || err.message);
            if (err.response?.status === 404) {
                setCommunityError("Table not found. It may have been deleted or the ID is incorrect.");
            } else {
                setCommunityError(err.response?.data?.error || "Failed to update table status.");
            }
        }
    };

    const handleCommunityTableDownload = async (table, category) => {
        try {
            setActionLoading((prev) => ({ ...prev, [`download-${table._id}`]: true }));
            const response = await axios.get(`/api/community/download-table/${table._id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${category.name.replace(/\s+/g, '_')}-v${table.version || '1.0'}.ct`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            setMessage({ text: "Table downloaded successfully.", type: "success" });
        } catch (error) {
            console.error("Error downloading community table:", error);
            setMessage({
                text: error.response?.data?.message || "Error downloading table",
                type: "error",
            });
        } finally {
            setActionLoading((prev) => ({ ...prev, [`download-${table._id}`]: false }));
        }
    };

    // Auto-clear messages after 5 seconds
    useEffect(() => {
        if (message) {
            const timeout = setTimeout(() => {
                setMessage("");
            }, 5000);
            return () => clearTimeout(timeout);
        }
    }, [message]);

    // Fetch data on mount
    useEffect(() => {
        checkAdminStatus();
        fetchCommunityCategories();
        fetchReports();
    }, [navigate]);

    return (
        <div className="admin-panel-container">
            <Helmet>
                <title>Community Management</title>
            </Helmet>
            <div className="admin-panel-content">
                <h2>Community Management</h2>
                {message && (
                    <p className={`adminpanel-message ${message.type}`}>
                        {message.text}
                    </p>
                )}
                {communityError && <p className="error-message">{communityError}</p>}
                <form onSubmit={handleCommunityCategorySubmit} className="category-form">
                    <input
                        type="text"
                        placeholder="Category Name"
                        value={categoryName}
                        onChange={(e) => setCategoryName(e.target.value)}
                        required
                    />
                    <input
                        type="text"
                        placeholder="Steam App ID"
                        value={categoryAppid}
                        onChange={(e) => setCategoryAppid(e.target.value)}
                    />
                    <label>
                        <input
                            type="checkbox"
                            checked={categoryIsNSFW}
                            onChange={(e) => setCategoryIsNSFW(e.target.checked)}
                        />
                        NSFW
                    </label>
                    <button type="submit" className="adminpanel-button">
                        {editingCategoryId ? "Update Category" : "Add Category"}
                    </button>
                    {editingCategoryId && (
                        <button
                            type="button"
                            className="adminpanel-button secondary"
                            onClick={() => {
                                setEditingCategoryId(null);
                                setCategoryName("");
                                setCategoryAppid("");
                                setCategoryIsNSFW(false);
                                setCommunityError("");
                            }}
                        >
                            Cancel
                        </button>
                    )}
                </form>
                {/* Reports Section */}
                <section className="reports-section">
                <h3>Community Table Reports</h3>

                {reportsLoading && <p className="reports-status">Loading reports…</p>}
                {reportsError && <p className="reports-status error">{reportsError}</p>}

                {!reportsLoading && !reportsError && (
                    <>
                    {reports.length === 0 ? (
                        <p className="reports-empty">No reports submitted.</p>
                    ) : (
                        <div className="reports-list">
                        {reports.map((r) => {
                            const cat = r.tableId?.categoryId;
                            const slug = cat?.slug;
                            const gameName = cat?.name || "Unknown";
                            const version = r.tableId?.version || "—";
                            const created = r.createdAt ? new Date(r.createdAt).toLocaleString() : "—";
                            return (
                            <article key={r._id} className="report-item">
                                <div className="report-header">
                                <span className={`reason-badge reason-${r.reason}`}>
                                    {reasonLabel[r.reason] || r.reason}
                                </span>
                                <time className="report-time">{created}</time>
                                </div>

                                <div className="report-main">
                                <div className="report-row">
                                    <strong>Game:</strong>
                                    {slug ? (
                                    <a
                                        href={`/community-tables/${slug}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {gameName}
                                    </a>
                                    ) : (
                                    <span>{gameName}</span>
                                    )}
                                </div>

                                <div className="report-row">
                                    <strong>Table Version:</strong>
                                    <span>v{version}</span>
                                </div>

                                {r.details && (
                                    <div className="report-details">
                                    <strong>Details:</strong>
                                    <p>{r.details}</p>
                                    </div>
                                )}
                                </div>
                            </article>
                            );
                        })}
                        </div>
                    )}
                    </>
                )}
                </section>
                <div className="community-categories">
                    {communityCategories.map((category) => (
                        <div key={category._id} className="category-item">
                            <h4>{category.name} (AppID: {category.appid || "N/A"})</h4>
                            <p>NSFW: {category.isNSFW ? "Yes" : "No"}</p>
                            <div className="category-actions">
                                <button
                                    onClick={() => handleEditCommunityCategory(category)}
                                    className="adminpanel-button modify"
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleDeleteCommunityCategory(category._id)}
                                    className="adminpanel-button delete"
                                >
                                    Delete
                                </button>
                            </div>
                            <div className="tables-list">
                                {category.tables && category.tables.length > 0 ? (
                                    category.tables.map((table) => (
                                        <div key={table._id} className="table-item">
                                            <p><strong>Status:</strong> {table.status}</p>
                                            <p><strong>Description:</strong> {table.description || "N/A"}</p>
                                            <p><strong>Uploader:</strong> {table.submitterId?.username || "Unknown"}</p>
                                            <p><strong>Submitted:</strong> {new Date(table.createdAt).toLocaleDateString()}</p>
                                            <p><strong>Version:</strong> {table.version || "1.0"}</p>
                                            <p><strong>Downloads:</strong> {table.downloadCount || 0}</p>
                                            {table.originalTableId && (
                                                <p><strong>Update for Table ID:</strong> {table.originalTableId}</p>
                                            )}
                                            <div className="action-bar">
                                                <button
                                                    onClick={() => handleVerifyCommunityTable(table._id, "approved")}
                                                    className="adminpanel-button approve"
                                                    disabled={table.status === "approved" || actionLoading[table._id]}
                                                    data-tooltip="Approve table"
                                                >
                                                    <FontAwesomeIcon icon={faCheck} />
                                                    {actionLoading[table._id] ? "Approving..." : "Approve"}
                                                </button>
                                                <button
                                                    onClick={() => handleVerifyCommunityTable(table._id, "rejected")}
                                                    className="adminpanel-button reject"
                                                    disabled={table.status === "rejected" || actionLoading[table._id]}
                                                    data-tooltip="Reject table"
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                    {actionLoading[table._id] ? "Rejecting..." : "Reject"}
                                                </button>
                                                <button
                                                    onClick={() => handleCommunityTableDownload(table, category)}
                                                    className="adminpanel-button download"
                                                    data-tooltip="Download .ct file"
                                                    disabled={actionLoading[`download-${table._id}`]}
                                                >
                                                    <FontAwesomeIcon icon={faDownload} />
                                                    {actionLoading[`download-${table._id}`] ? "Downloading..." : "Download"}
                                                </button>
                                                <button
                                                    onClick={() => handleViewTableDetails(table)}
                                                    className="adminpanel-button view-details"
                                                    data-tooltip="View table details"
                                                >
                                                    <FontAwesomeIcon icon={faEye} /> Details
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p>No tables for this category.</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                {selectedTableDetails && (
                    <div className="confirmation-modal">
                        <div className="modal-content">
                            <h3>Table Details</h3>
                            <p><strong>Status:</strong> {selectedTableDetails.status}</p>
                            <p><strong>Description:</strong> {selectedTableDetails.description || "N/A"}</p>
                            <p><strong>Uploader:</strong> {selectedTableDetails.submitterId?.username || "Unknown"}</p>
                            <p><strong>Submitted:</strong> {new Date(selectedTableDetails.createdAt).toLocaleDateString()}</p>
                            <p><strong>Version:</strong> {selectedTableDetails.version || "1.0"}</p>
                            <p><strong>File URL:</strong> {selectedTableDetails.fileUrl}</p>
                            <p><strong>Submitter IP:</strong> {selectedTableDetails.submitterIp || "N/A"}</p>
                            <p><strong>Downloads:</strong> {selectedTableDetails.downloadCount || 0}</p>
                            {selectedTableDetails.originalTableId && (
                                <p><strong>Update for Table ID:</strong> {selectedTableDetails.originalTableId}</p>
                            )}
                            <div className="modal-button-container">
                                <button
                                    onClick={() => handleVerifyCommunityTable(selectedTableDetails._id, "approved")}
                                    className="adminpanel-button approve"
                                    disabled={selectedTableDetails.status === "approved" || actionLoading[selectedTableDetails._id]}
                                >
                                    <FontAwesomeIcon icon={faCheck} />
                                    {actionLoading[selectedTableDetails._id] ? "Approving..." : "Approve"}
                                </button>
                                <button
                                    onClick={() => handleVerifyCommunityTable(selectedTableDetails._id, "rejected")}
                                    className="adminpanel-button reject"
                                    disabled={selectedTableDetails.status === "rejected" || actionLoading[selectedTableDetails._id]}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                    {actionLoading[selectedTableDetails._id] ? "Rejecting..." : "Reject"}
                                </button>
                                <button onClick={closeTableDetailsModal} className="adminpanel-button secondary">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CommunityManagement;